import React, { useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * VERIFY PAGE — Backend Integration Guide
 *
 * EMAIL VERIFICATION
 *   The Verify button submits the entered code.
 *   To wire up the backend: POST /api/auth/verify-email  { email, code: code.join('') }
 *   On success: navigate to /dashboard (user is now fully authenticated)
 *   On failure: show an error message (e.g. "Invalid or expired code")
 *
 * RESEND CODE
 *   → "Didn't Get A Code?" button currently does nothing
 *   → Wire to: POST /api/auth/resend-verification  { email }
 *   → Consider adding a cooldown timer (e.g. 60s) to prevent spamming
 *
 * EMAIL
 *   → email is passed via location.state from the register flow
 *   → Make sure Landing always passes: navigate('/verify', { state: { email } })
 */

const Verify = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const email = location.state?.email || "test@gmail.com";
  
  const [code, setCode] = useState(['', '', '', '', '', '']);
  
  const inputRefs = useRef([]);

  const handleChange = (index, value) => {
    // Only allow numbers
    if (!/^\d*$/.test(value)) return;
    
    const newCode = [...code];
    // Take only the last character entered
    newCode[index] = value.substring(value.length - 1);
    setCode(newCode);

    // Move to next box if value is entered 
    if (value && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Move back on backspace if current box is empty
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  return (
    <div className="min-h-screen bg-[#050d1f] text-white flex flex-col items-center p-8 relative overflow-hidden font-sans">
      {/* Background Glows */}
      <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-[#4d7cff]/10 rounded-full blur-[120px]" />
      
      {/* Navbar Area */}
      <div className="w-full max-w-7xl flex justify-between items-center mb-20 z-10">
        <h1 className="text-3xl font-bold text-[#4d7cff] tracking-tight">Project</h1>
        <button onClick={() => navigate('/')} className="text-sm font-medium hover:text-[#4d7cff] transition-colors">
          Register
        </button>
      </div>

      {/* Main Card */}
      <div className="relative z-10 w-full max-w-3xl"> 
        <div className="absolute -inset-0.5 bg-[#4d7cff]/20 rounded-[40px] blur-sm"></div>
        
        <div className="relative bg-[#0c1526]/90 backdrop-blur-xl border border-white/[0.08] rounded-[40px] p-12 lg:p-16 flex flex-col items-center shadow-2xl">
          <h2 className="text-3xl font-bold text-[#4d7cff] mb-4 text-center">Enter Verification Code</h2>
          <p className="text-white/50 mb-12 text-center">
            We've sent a 6-digit code to <span className="text-[#4d7cff]">{email}</span>
          </p>

          {/* Inputs */}
          <div className="flex gap-2 sm:gap-4 mb-12">
            {code.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className="w-12 h-16 sm:w-20 sm:h-24 bg-[#1e1e1e] border border-white/5 rounded-2xl sm:rounded-3xl text-2xl sm:text-4xl text-center font-bold focus:border-[#4d7cff] focus:ring-1 focus:ring-[#4d7cff] outline-none transition-all"
                maxLength={1}
                inputMode="numeric"
              />
            ))}
          </div>

          <button className="text-sm text-white/40 hover:text-white transition-colors mb-8">
            Didn't Get A Code?
          </button>

          <button
            onClick={() => {}}
            className="w-full max-w-sm bg-[#2563eb] hover:bg-[#1d4ed8] text-white font-bold py-4 rounded-full shadow-[0_0_20px_rgba(37,99,235,0.3)] transition-all transform hover:scale-[1.02]"
          >
            Verify
          </button>
        </div>
      </div>
    </div>
  );
};

export default Verify;